import React, { useMemo } from "react";

import TemplateFormScreen from "./TemplateFormScreen";
import useTranslations from "../hooks/useTranslations";
import useViewer from "../hooks/useViewer";

import { useQueryParams, BooleanParam, StringParam } from "use-query-params";
import {
  Container,
  ScreenContainer,
  ScreenHeader,
  Button,
  Link,
  EmptyState,
} from "../components/wombat-ui";
import { EditIcon } from "../components/icons";
import { useBreadcrumbs } from "../hooks/useBreadcrumbs";
import { useGetTemplate } from '../graphql/generated/definitions';

const EditTemplateScreen = ({ match }) => {
  const t = useTranslations();

  const id = match?.params?.id;
  const { data, loading } = useGetTemplate({ variables: { id } });

  const [{ copied, reportId, reviewId }] = useQueryParams({
    copied: BooleanParam,
    reportId: StringParam,
    reviewId: StringParam,
  });

  const template = useMemo(() => {
    const template = data?.template;

    if (!template) {
      return null;
    }

    // NOTE: The direct string comparison should be replaced by a proper QS parser when needed
    if (copied) {
      return {
        ...template,
        id: null,
        default: false,
        title: t("templates.copyOf", {
          originalTitle: template.title,
        }) as string,
      };
    } else {
      return template;
    }
  }, [data?.template, copied]);

  useBreadcrumbs([
    {
      to: "/templates",
      text: t("templates.templates") as string,
    },
    {
      to: `/templates/${match?.params?.id}/edit`,
      text: template?.title,
    },
  ]);

  const handleDuplicate = useMemo(() => `/templates/${id}/copy?copied=1`, [id]);

  const { isCapableOfInsertingTemplates } = useViewer();

  const canInsert = useMemo(() => {
    if (copied) {
      return isCapableOfInsertingTemplates;
    }
    return data?.template?.canInsert;
  }, [isCapableOfInsertingTemplates, copied, data?.template]);

  const returnUrl = useMemo(() => {
    if (reportId) {
      return `/reports/${reportId}`;
    }
    if (reviewId) {
      return `/reviews/${reviewId}`;
    }
    return "/templates";
  }, [reportId, reviewId]);

  return (
    <ScreenContainer
      loading={loading}
      error={
        !canInsert ? (
          <EmptyState
            title={t("network.unauthorized") as string}
            action={
              <Link variant="button" to="/">
                {t("network.backToHome")}
              </Link>
            }
          />
        ) : undefined
      }
    >
      {template && !loading && (
        <>
          <ScreenHeader
            title={template.title}
            icon={EditIcon}
            actions={[
              !copied && !reviewId && isCapableOfInsertingTemplates && (
                <Link
                  key="duplicate"
                  variant="button-subtle"
                  to={handleDuplicate}
                >
                  {t("forms.actions.duplicate")}
                </Link>
              ),
              <Button key="update" type="submit" form="templateForm">
                {t("forms.actions.save")}
              </Button>,
            ]}
          />
          <Container>
            <TemplateFormScreen
              template={template}
              returnUrl={returnUrl}
            />
          </Container>
        </>
      )}
    </ScreenContainer>
  );
};

export default EditTemplateScreen;
